import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserApi } from '../../services/api/UserApi';
import './Settings.css';
import './SettingsTheme.css';

function ImpersonateUser() {
  const { startImpersonation, isImpersonating } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inlineLoading, setInlineLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [confirmTarget, setConfirmTarget] = useState(null); // user object pending confirmation
  const [impersonating, setImpersonating] = useState(false); // API call in progress

  const currentRequestRef = useRef(null);

  // Debounce search input (350ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const loadUsers = useCallback(async () => {
    setError(null);

    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }
    const controller = new AbortController();
    currentRequestRef.current = controller;

    if (!hasLoadedOnce) setLoading(true);
    else setInlineLoading(true);

    try {
      const response = await UserApi.getAllUsers({
        page,
        size,
        sortBy: 'userId',
        sortDir: 'asc',
        search: debouncedSearch,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      if (response.ok) {
        const data = await response.json();
        // Filter out admin users from the results (they cannot be impersonated)
        const nonAdminUsers = (data.users || []).filter(
          (u) => !u.roles || !u.roles.some((r) => r.roleName === 'admin')
        );
        setUsers(nonAdminUsers);
        setTotalPages(data.totalPages || 1);
        setTotalUsers(data.totalUsers || 0);
        setHasLoadedOnce(true);
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError('Error loading users');
      console.error('ImpersonateUser: Error loading users', err);
    } finally {
      setLoading(false);
      setInlineLoading(false);
    }
  }, [page, size, debouncedSearch, hasLoadedOnce]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleImpersonate = async (targetUser) => {
    setImpersonating(true);
    setError(null);

    try {
      const userId = targetUser.userId || targetUser.id;
      const response = await UserApi.impersonateUser(userId);

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Impersonation failed (${response.status})`);
      }

      const impersonationData = await response.json();

      await startImpersonation(impersonationData, {
        userId,
        firstName: targetUser.firstName,
        lastName: targetUser.lastName,
        email: targetUser.email,
      });

      // Navigation will happen naturally — the impersonation banner will appear
      // and the user sees the app as the target user. Redirect to dashboard.
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('ImpersonateUser: Impersonation failed', err);
      setError(err.message || 'Failed to start impersonation');
    } finally {
      setImpersonating(false);
      setConfirmTarget(null);
    }
  };

  if (isImpersonating) {
    return (
      <div className="settings-page-content">
        <div className="settings-page-header">
          <h2>Sign In As User</h2>
        </div>
        <div className="info-panel" style={{ marginTop: '1rem' }}>
          <p>You are currently impersonating another user. Exit impersonation first to use this feature.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="settings-page-content">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>Sign In As User</h2>
      </div>

      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Select a user to view the application as them. Admin users cannot be impersonated.
        Impersonation sessions expire after 1 hour.
      </p>

      {error && <div className="error-message">{error}</div>}

      {/* Confirmation dialog */}
      {confirmTarget && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => !impersonating && setConfirmTarget(null)}
        >
          <div
            style={{
              background: 'var(--surface-primary, #fff)',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Confirm Impersonation</h3>
            <p>
              You are about to view the application as{' '}
              <strong>
                {confirmTarget.firstName} {confirmTarget.lastName}
              </strong>{' '}
              ({confirmTarget.email}).
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              This session will last up to 1 hour. You can exit impersonation at any time using
              the banner at the top of the page.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                className="btn-secondary"
                onClick={() => setConfirmTarget(null)}
                disabled={impersonating}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => handleImpersonate(confirmTarget)}
                disabled={impersonating}
                style={{ backgroundColor: '#e67e22', borderColor: '#e67e22' }}
              >
                {impersonating ? 'Signing in...' : 'Sign In As User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="search-container" style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border: '1px solid var(--border-primary, #ddd)',
            fontSize: '0.9rem',
          }}
        />
        {inlineLoading && (
          <span style={{ marginLeft: '0.5rem', color: 'var(--text-secondary)' }}>Loading...</span>
        )}
      </div>

      {/* Results table */}
      {users.length === 0 ? (
        <div className="empty-state">
          <p>No non-admin users found{debouncedSearch ? ` matching "${debouncedSearch}"` : ''}.</p>
        </div>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-primary, #eee)' }}>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Roles</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const userId = u.userId || u.id;
                const name =
                  u.firstName || u.lastName
                    ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                    : u.email?.split('@')[0] || `User #${userId}`;
                return (
                  <tr key={userId} style={{ borderBottom: '1px solid var(--border-primary, #eee)' }}>
                    <td style={tdStyle}>{name}</td>
                    <td style={tdStyle}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        {u.email}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {(u.roles || []).map((r) => (
                        <span
                          key={r.roleId || r.roleName}
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            marginRight: '4px',
                            backgroundColor: 'var(--surface-secondary, #f0f0f0)',
                            color: 'var(--text-primary)',
                          }}
                        >
                          {r.roleName}
                        </span>
                      ))}
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.85rem',
                        }}
                      >
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: u.isActive ? '#27ae60' : '#95a5a6',
                          }}
                        />
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button
                        className="btn-primary"
                        onClick={() => setConfirmTarget(u)}
                        style={{
                          padding: '4px 12px',
                          fontSize: '0.8rem',
                          backgroundColor: '#e67e22',
                          borderColor: '#e67e22',
                        }}
                      >
                        Sign In As
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '1rem',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Page {page} of {totalPages} ({totalUsers} total users)
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  className="btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                >
                  First
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                >
                  Next
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left',
  padding: '0.75rem 0.5rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
};

const tdStyle = {
  padding: '0.75rem 0.5rem',
  verticalAlign: 'middle',
};

export default ImpersonateUser;
